/**
 * Reference tables (Tabelle, proposal §5): the lists the modules rely on, with
 * stable codes, editable descriptions, versions valid from a date and history.
 *
 * - System tables are declared here: the program decides their columns and
 *   uses their codes in its rules. They cannot be deleted.
 * - Free tables are created by administrators with their own columns; a free
 *   table can be a classification of the registry subjects.
 *
 * Every table has versions. A "direct" table is edited in place (every change
 * goes to the history); a "versioned" table changes only through a draft
 * version activated from a date, so documents keep the version they used.
 */
import { z } from 'zod';
import { addDays } from './dates.js';
import { parseDecimal } from './money.js';
import { isoDateSchema, optionalText, requiredText } from './validation.js';
export const COLUMN_TYPES = [
    'text',
    'number',
    'money',
    'boolean',
    'date',
    'select',
    'tags',
];
export const COLUMN_TYPE_LABELS = {
    text: 'Testo',
    number: 'Numero',
    money: 'Importo',
    boolean: 'Sì/No',
    date: 'Data',
    select: 'Scelta da elenco',
    tags: 'Scelta multipla',
};
/** The entries a lookup column offers, given the other values of the entry. */
export function lookupOptions(column, values, lookups) {
    const lookup = column.lookup;
    if (!lookup)
        return [];
    const parent = lookup.childOf ? String(values[lookup.childOf] ?? '') : null;
    return (lookups[lookup.table] ?? []).filter((entry) => !(lookup.exclude ?? []).includes(entry.code) &&
        Object.entries(lookup.where ?? {}).every(([key, value]) => (entry.values?.[key] ?? null) === value) &&
        (!lookup.topLevel || !entry.parent) &&
        (parent === null || entry.parent === parent));
}
/**
 * Turns the values of the lookup columns into codes: a code or a name of the
 * other table, in any case. Unknown values, and a sub-voice that does not
 * belong to its voice, become errors by column.
 */
export function resolveLookups(columns, values, lookups) {
    const result = { ...values };
    const errors = {};
    // Parents first: a sub-voice is checked against the voice already resolved.
    const ordered = [...columns].sort((a, b) => Number(!!a.lookup?.childOf) - Number(!!b.lookup?.childOf));
    for (const column of ordered) {
        const raw = result[column.key];
        if (!column.lookup || raw === null || raw === undefined || raw === '')
            continue;
        const wanted = String(raw).trim().toLowerCase();
        const options = lookupOptions(column, result, lookups);
        const found = options.find((entry) => entry.code.toLowerCase() === wanted || entry.description.toLowerCase() === wanted);
        if (found)
            result[column.key] = found.code;
        else
            errors[column.key] = `Voce non prevista: ${String(raw).trim()}`;
    }
    return { values: result, errors };
}
/**
 * The column that puts a table on two levels: a lookup into the table itself
 * (the «Tipologia padre» of the types of line). Undefined for a flat table.
 */
export function parentColumn(tableKey, columns) {
    // A lookup into the table itself towards its first level: «Sostituisce» of a
    // price list points to the table too, but does not make two levels.
    return columns.find((column) => column.lookup?.table === tableKey && column.lookup.topLevel);
}
/**
 * The group an entry is ordered and shown within (decision of 29/09/2026):
 * the values of the table's `orderGroups` columns; '' for a table without.
 */
export function orderGroupOf(tableKey, values) {
    const columns = SYSTEM_TABLES.find((table) => table.key === tableKey)?.orderGroups ?? [];
    return columns
        .map((column) => {
        const value = values[column];
        return typeof value === 'string' ? value.trim().toUpperCase() : '';
    })
        .join('|');
}
/**
 * The entries in blocks (decision of 29/09/2026): each entry of the first
 * level followed by its sub-entries, in their order. An entry whose parent is
 * not in the list, or is itself a sub-entry, counts as first level.
 */
export function entryBlocks(entries) {
    const heads = new Set(entries
        .filter((entry) => !entry.parent || !entries.some((other) => other.code === entry.parent))
        .map((entry) => entry.code));
    const isChild = (entry) => !!entry.parent && heads.has(entry.parent);
    return entries
        .filter((entry) => !isChild(entry))
        .map((head) => [
        head,
        ...entries.filter((entry) => isChild(entry) && entry.parent === head.code),
    ]);
}
/** The codes in the order of the blocks: every sub-entry right under its parent. */
export function arrangeEntries(entries) {
    return entryBlocks(entries).flatMap((block) => block.map((entry) => entry.code));
}
/**
 * The order after moving an entry one place: an entry of the first level
 * moves with all its sub-entries, a sub-entry moves only among its siblings,
 * an entry of a group (the costs of a type) only among the entries of its
 * group, past the others. Null when it cannot move that way.
 */
export function moveEntry(entries, code, direction) {
    const blocks = entryBlocks(entries);
    const step = direction === 'up' ? -1 : 1;
    const swap = (items, from, to) => {
        [items[from], items[to]] = [items[to], items[from]];
    };
    const head = blocks.findIndex((block) => block[0].code === code);
    if (head >= 0) {
        const group = blocks[head][0].group ?? '';
        let target = head + step;
        while (target >= 0 && target < blocks.length && (blocks[target][0].group ?? '') !== group) {
            target += step;
        }
        if (target < 0 || target >= blocks.length)
            return null;
        swap(blocks, head, target);
    }
    else {
        const block = blocks.find((items) => items.some((entry) => entry.code === code));
        if (!block)
            return null;
        const index = block.findIndex((entry) => entry.code === code);
        const target = index + step;
        // Index 0 is the parent: a sub-entry never leaves its block.
        if (target < 1 || target >= block.length)
            return null;
        swap(block, index, target);
    }
    return blocks.flatMap((block) => block.map((entry) => entry.code));
}
/* --- Price lists (docs/analisi/proposta-listini.md, 01/10/2026) ------------- */
/** The table of the price lists: names, kind and validity. */
export const PRICE_LISTS_TABLE = 'listini';
/**
 * How a voice of the Listino Passività counts: an amount (the costs and the
 * fees «a corpo») or a percentage of the taxable amount of the quote.
 */
export const COST_CALCS = ['Importo', 'Percentuale'];
/** The selling prices of the price lists of kind Attività. */
export const ACTIVITY_PRICES_TABLE = 'listini-attivita';
export const PRICE_LIST_KINDS = ['Attività', 'Passività'];
/** The Base list of each kind: always there, complete, the fallback of the others. */
export const BASE_PRICE_LIST = {
    Attività: 'BASE-ATT',
    Passività: 'BASE-PAS',
};
/** The binding of a table to the price lists; undefined for the other tables. */
export function priceListBinding(tableKey) {
    return SYSTEM_TABLES.find((table) => table.key === tableKey)?.priceList;
}
/** The list a voice belongs to: the Base when the column is empty. */
export function listOf(entry, binding) {
    const value = entry.values[binding.listColumn];
    return typeof value === 'string' && value ? value : BASE_PRICE_LIST[binding.kind];
}
function hasAmount(entry, binding) {
    const value = entry.values[binding.amountColumn];
    return value !== null && value !== undefined && value !== '';
}
/**
 * The voices a list offers, in the order given: its own voices and, for a
 * list other than the Base, the voices of the Base it does not have. A voice
 * of the list without an amount keeps its place and takes the amount of the
 * Base voice it replaces (`fallback`): a list of a client is filled only
 * where it differs.
 */
export function listEntries(entries, list, binding) {
    const base = BASE_PRICE_LIST[binding.kind];
    const own = entries.filter((entry) => listOf(entry, binding) === list);
    if (list === base)
        return own.map((entry) => ({ entry, origin: 'own' }));
    const replacedBy = (entry) => {
        const code = entry.values[binding.replacesColumn];
        return typeof code === 'string' && code !== '' ? code : null;
    };
    const replaced = new Set(own.map(replacedBy).filter((code) => code !== null));
    return entries.flatMap((entry) => {
        const of = listOf(entry, binding);
        if (of === list) {
            const code = replacedBy(entry);
            const fallback = !hasAmount(entry, binding) && code
                ? entries.find((item) => item.code === code && listOf(item, binding) === base)
                : undefined;
            return [{ entry, origin: 'own', ...(fallback ? { fallback } : {}) }];
        }
        if (of === base && !replaced.has(entry.code))
            return [{ entry, origin: 'base' }];
        return [];
    });
}
/**
 * The price of a list for a line (proposal §2.4): the voice of the list with
 * type, sub-type and unit; then the one of the list for the whole type; then
 * the same two in the Base. Null when none has an amount: the price is
 * written by hand.
 */
export function listPrice(entries, list, binding, line) {
    const text = (value) => (typeof value === 'string' ? value : '');
    const voices = listEntries(entries, list, binding).filter((voice) => text(voice.entry.values.type) === line.typeCode &&
        text(voice.entry.values.unit) === line.unitCode);
    const candidates = [
        (voice) => voice.origin === 'own' && text(voice.entry.values.subType) === (line.subTypeCode ?? ''),
        (voice) => voice.origin === 'own' && text(voice.entry.values.subType) === '',
        (voice) => voice.origin === 'base' && text(voice.entry.values.subType) === (line.subTypeCode ?? ''),
        (voice) => voice.origin === 'base' && text(voice.entry.values.subType) === '',
    ];
    for (const matches of candidates) {
        const voice = voices.find((item) => matches(item) && listedAmount(item, binding) !== null && listedAmount(item, binding) !== '');
        if (voice)
            return { amount: listedAmount(voice, binding), voice: voice.entry };
    }
    return null;
}
/** The amount a voice of a list counts with: its own, or the Base one until it has one. */
export function listedAmount(voice, binding) {
    return voice.fallback && !hasAmount(voice.entry, binding)
        ? (voice.fallback.values[binding.amountColumn] ?? null)
        : (voice.entry.values[binding.amountColumn] ?? null);
}
/**
 * The values of the voices of a new list made from another (Duplica): with
 * the data, the amounts too; without, the voices only. A copy of the Base
 * replaces the voices it comes from; a copy of another list replaces what
 * that one replaced.
 */
export function duplicatedValues(entries, from, to, withData, binding) {
    const base = BASE_PRICE_LIST[binding.kind];
    return entries
        .filter((entry) => listOf(entry, binding) === from)
        .map((entry) => ({
        from: entry.code,
        values: {
            ...entry.values,
            [binding.listColumn]: to,
            [binding.replacesColumn]: from === base ? entry.code : (entry.values[binding.replacesColumn] ?? null),
            ...(withData ? {} : { [binding.amountColumn]: null }),
        },
    }));
}
/** Body of `POST /api/tables/listini/entries/:code/duplicate`. */
export const duplicateListSchema = z.object({
    name: requiredText(200),
    validFrom: isoDateSchema.nullish().transform((value) => value ?? null),
    validTo: isoDateSchema.nullish().transform((value) => value ?? null),
    withData: z.boolean(),
});
/** The permission a table needs besides tabelle.read; null for most tables. */
export function tableRequires(key) {
    return SYSTEM_TABLES.find((table) => table.key === key)?.requires ?? null;
}
/** The permission changing a table needs besides tabelle.write; null for most tables. */
export function tableWriteRequires(key) {
    return SYSTEM_TABLES.find((table) => table.key === key)?.writeRequires ?? null;
}
export const TABLE_GROUPS = [
    'Anagrafica',
    'Documenti',
    'Preventivatore',
    'Tesoreria',
    'Organizzazione',
    'Operations',
    'Tabelle libere',
];
/** Data a role of the registry may require (proposal §2.2). */
export const ROLE_DATA = [
    'Termini di pagamento',
    'Metodo di pagamento',
    'Priorità',
    'Conto riclassificato',
    'Centro di costo',
    'Conto preferenziale',
    'IBAN',
];
export const SYSTEM_TABLES = [
    {
        key: 'tipologie-soggetto',
        group: 'Anagrafica',
        name: 'Tipologie soggetto',
        description: 'Tipo giuridico del soggetto: decide quale identificativo fiscale è obbligatorio.',
        mode: 'direct',
        origin: 'Allineata a Gestify',
        columns: [
            {
                key: 'taxId',
                label: 'Identificativo obbligatorio',
                type: 'select',
                options: ['P.IVA', 'Codice fiscale', 'P.IVA o codice fiscale'],
            },
            { key: 'gestify', label: 'Codice Gestify', type: 'text' },
        ],
    },
    {
        key: 'ruoli-anagrafici',
        group: 'Anagrafica',
        name: 'Ruoli anagrafici',
        description: 'I ruoli che un soggetto può avere, con prefisso dei codici, verso per la Tesoreria e dati richiesti dalla scheda.',
        mode: 'direct',
        origin: 'Proposta §2.2',
        columns: [
            { key: 'prefix', label: 'Prefisso', type: 'text' },
            {
                key: 'direction',
                label: 'Verso',
                type: 'select',
                options: ['Incasso', 'Pagamento', 'Entrambi', 'Nessuno'],
            },
            { key: 'requiredData', label: 'Dati richiesti', type: 'tags', options: [...ROLE_DATA] },
            { key: 'reserved', label: 'Riservato', type: 'boolean' },
            { key: 'gestify', label: 'Gruppo Gestify', type: 'text' },
        ],
    },
    {
        key: 'priorita',
        group: 'Anagrafica',
        name: 'Priorità',
        description: 'Ordine con cui la Tesoreria propone i pagamenti.',
        mode: 'direct',
        origin: 'CSV Tesoreria',
        columns: [],
    },
    {
        key: 'referenti',
        group: 'Anagrafica',
        name: 'Referenti',
        description: 'Referente interno di preventivi e commesse. Un referente disattivato resta sui documenti già emessi.',
        mode: 'direct',
        origin: 'CSV Gestione Commesse',
        autoCode: 'REF',
        columns: [{ key: 'email', label: 'E-mail', type: 'text' }],
    },
    {
        key: 'termini-pagamento',
        group: 'Documenti',
        name: 'Termini di pagamento',
        description: 'Scadenza di incassi e pagamenti: mesi, giorni, fine mese.',
        mode: 'direct',
        origin: 'Gestify',
        columns: [
            { key: 'months', label: 'Mesi', type: 'number' },
            { key: 'days', label: 'Giorni', type: 'number' },
            { key: 'endOfMonth', label: 'Fine mese', type: 'boolean' },
        ],
    },
    {
        key: 'metodi-pagamento',
        group: 'Documenti',
        name: 'Metodi di pagamento',
        description: 'Come si paga o si incassa.',
        mode: 'direct',
        origin: 'Gestify',
        columns: [],
    },
    {
        key: 'aliquote-iva',
        group: 'Documenti',
        name: 'Aliquote IVA',
        description: 'Aliquote e nature IVA di preventivi e fatture.',
        mode: 'direct',
        origin: 'Gestify',
        columns: [
            { key: 'rate', label: '%', type: 'number' },
            { key: 'nature', label: 'Natura', type: 'text' },
        ],
    },
    {
        key: 'unita-misura',
        group: 'Preventivatore',
        name: 'Unità di misura',
        description: 'Come si conta una riga di preventivo: giorni, ore, licenze.',
        mode: 'direct',
        origin: 'Proposta §4',
        columns: [],
    },
    {
        key: 'listino-licenze',
        group: 'Preventivatore',
        name: 'Listino licenze',
        description: 'Scaglioni di licenze (PAX): il codice della voce è la soglia dello scaglione. Il setup si paga a gradini, le licenze a fasce come gli scaglioni IRPEF. Si modifica direttamente, come le altre tabelle: ogni cambiamento resta nello storico e i preventivi già fatti tengono gli importi con cui sono nati.',
        mode: 'direct',
        origin: 'Foglio Parametri di Gestione Commesse',
        // The aliases are the headings of that sheet: the file imports in one click.
        codeAliases: ['PAX'],
        columns: [
            { key: 'setup', label: 'Setup', type: 'money', aliases: ['SETUP'] },
            { key: 'band', label: 'Ampiezza fascia', type: 'number', aliases: ['LIC/INC'] },
            {
                key: 'perLicence',
                label: '€ / licenza / anno',
                type: 'money',
                aliases: ['€/LIC/Y', 'LIC/Y'],
            },
            {
                key: 'premium',
                label: 'Premium per licenza / anno',
                type: 'money',
                aliases: ['Licenze premium anno', 'premium'],
            },
        ],
    },
    {
        key: 'tipologie-riga',
        group: 'Preventivatore',
        name: 'Tipologie di riga',
        description: 'Cosa si vende, su due livelli: una voce senza «Tipologia padre» è di primo livello, le altre sono le sue sottotipologie. Con «Testo libero» la riga chiede anche una descrizione; con «Compila da listino» (solo sul primo livello) il preventivo propone una griglia con tutte le sottotipologie.',
        mode: 'direct',
        origin: 'Tipologie K-Rev',
        columns: [
            {
                key: 'parent',
                label: 'Tipologia padre',
                type: 'text',
                // Two levels: the parent is a voice of this same table, of the first level.
                lookup: { table: 'tipologie-riga', topLevel: true },
            },
            { key: 'freeText', label: 'Testo libero', type: 'boolean' },
            { key: 'unit', label: 'Unità predefinita', type: 'text', lookup: { table: 'unita-misura' } },
            // Proposal Listini §7: the quote fills this type with a grid of all its sub-types.
            { key: 'fromList', label: 'Compila da listino', type: 'boolean' },
            {
                key: 'check',
                label: 'Controllo',
                type: 'select',
                options: ['Margine', 'Sconto su listino'],
            },
        ],
    },
    {
        key: PRICE_LISTS_TABLE,
        group: 'Preventivatore',
        name: 'Listini',
        description: 'I listini di prezzi (Attività) e di costi (Passività), con la loro validità. Il Base di ogni tipo c’è sempre ed è completo; gli altri contengono solo le voci diverse e il resto lo prendono dal Base. Un listino nuovo nasce con «Duplica» da quello da cui parte.',
        mode: 'direct',
        origin: 'Decisione del 01/10/2026',
        autoCode: 'LIS',
        columns: [
            { key: 'kind', label: 'Tipo', type: 'select', options: [...PRICE_LIST_KINDS] },
            { key: 'validFrom', label: 'Valido dal', type: 'date' },
            { key: 'validTo', label: 'Valido fino al', type: 'date' },
        ],
    },
    {
        // The key stays «costi»: the codes of its voices are in the quotes already made.
        key: 'costi',
        group: 'Preventivatore',
        name: 'Listino Passività',
        description: 'Costi ipotetici per stimare il margine del preventivo, per listino: il Base è completo, gli altri listini contengono solo le voci con un costo diverso e il resto lo prendono dal Base. La descrizione è il profilo (es. «Formatore senior»); tipologia, sottotipologia e unità si scelgono dalle tabelle Tipologie di riga e Unità di misura (senza sottotipologia il costo vale per tutta la tipologia). Una voce senza costo unitario non viene proposta finché non lo si scrive.',
        mode: 'direct',
        origin: 'Decisione del 26/09/2026, listini dal 01/10/2026',
        autoCode: 'COS',
        orderGroups: ['type', 'subType'],
        requires: 'preventivatore.costs',
        priceList: {
            kind: 'Passività',
            listColumn: 'list',
            replacesColumn: 'replaces',
            amountColumn: 'cost',
        },
        columns: [
            {
                key: 'list',
                label: 'Listino',
                type: 'text',
                lookup: { table: PRICE_LISTS_TABLE, where: { kind: 'Passività' } },
            },
            {
                key: 'replaces',
                label: 'Sostituisce',
                type: 'text',
                // A voice of a list other than the Base takes the place of a Base voice.
                lookup: { table: 'costi', where: { list: 'BASE-PAS' } },
            },
            {
                key: 'type',
                label: 'Tipologia',
                type: 'text',
                // The licences have no costs: their light looks at the price list.
                lookup: { table: 'tipologie-riga', topLevel: true, exclude: ['LIC'] },
            },
            {
                key: 'subType',
                label: 'Sottotipologia',
                type: 'text',
                lookup: { table: 'tipologie-riga', childOf: 'type' },
            },
            { key: 'unit', label: 'Unità', type: 'text', lookup: { table: 'unita-misura' } },
            { key: 'cost', label: 'Costo unitario', type: 'money' },
            // The fees (proposal Listini §6): a percentage of the taxable amount of the quote.
            { key: 'calc', label: 'Calcolo', type: 'select', options: [...COST_CALCS] },
            { key: 'percent', label: 'Percentuale', type: 'number' },
        ],
    },
    {
        key: ACTIVITY_PRICES_TABLE,
        group: 'Preventivatore',
        name: 'Listini Attività',
        description: 'Prezzi di vendita per listino: una voce è tipologia, sottotipologia (facoltativa: senza vale per tutta la tipologia), unità e prezzo. Il Base è completo; gli altri listini contengono solo i prezzi diversi e il resto lo prendono dal Base. Nel preventivo il prezzo si propone da solo e resta modificabile. Le licenze hanno il loro Listino licenze.',
        mode: 'direct',
        origin: 'Decisione del 01/10/2026',
        autoCode: 'PRZ',
        orderGroups: ['type', 'subType'],
        priceList: {
            kind: 'Attività',
            listColumn: 'list',
            replacesColumn: 'replaces',
            amountColumn: 'price',
            uniqueBy: ['type', 'subType', 'unit'],
        },
        columns: [
            {
                key: 'list',
                label: 'Listino',
                type: 'text',
                lookup: { table: PRICE_LISTS_TABLE, where: { kind: 'Attività' } },
            },
            {
                key: 'replaces',
                label: 'Sostituisce',
                type: 'text',
                lookup: { table: ACTIVITY_PRICES_TABLE, where: { list: 'BASE-ATT' } },
            },
            {
                key: 'type',
                label: 'Tipologia',
                type: 'text',
                // The licences have their own price list, by thresholds.
                lookup: { table: 'tipologie-riga', topLevel: true, exclude: ['LIC'] },
            },
            {
                key: 'subType',
                label: 'Sottotipologia',
                type: 'text',
                lookup: { table: 'tipologie-riga', childOf: 'type' },
            },
            { key: 'unit', label: 'Unità', type: 'text', lookup: { table: 'unita-misura' } },
            { key: 'price', label: 'Prezzo', type: 'money' },
        ],
    },
    {
        key: 'soglie-margine',
        group: 'Preventivatore',
        name: 'Soglie di margine',
        description: 'Semaforo del margine nei Costi previsti: VERDE dalla sua percentuale in su, GIALLO dalla sua, rosso sotto.',
        mode: 'direct',
        origin: 'Decisione del 26/09/2026',
        columns: [{ key: 'percent', label: 'Margine %', type: 'number' }],
    },
    {
        key: 'soglie-licenze',
        group: 'Preventivatore',
        name: 'Soglie licenze',
        description: 'Semaforo delle licenze sul prezzo rispetto al listino: VERDE fino a questo sconto (0 = prezzo di listino o superiore), GIALLO fino a questo, rosso oltre. Lo sconto si misura senza il premium; il rinnovo si confronta con il suo listino.',
        mode: 'direct',
        origin: 'Decisione del 26/09/2026',
        columns: [{ key: 'percent', label: 'Sconto massimo %', type: 'number' }],
    },
    // Tesoreria (docs/analisi/proposta-tesoreria.md, specification appendix 13):
    // the rules read the columns of these tables, never the codes.
    {
        key: 'tipi-documento',
        group: 'Tesoreria',
        name: 'Tipi documento',
        description: 'Che cosa è un documento contabile: fattura, nota di credito, F24, stipendi… Le colonne dicono come lo trattano scadenze, import delle fatture e note di credito.',
        mode: 'direct',
        origin: 'Tesoreria (TIPDOC)',
        codeAliases: ['TipDoc', 'COD'],
        columns: [
            { key: 'dueDates', label: 'Concorre alle scadenze', type: 'boolean' },
            { key: 'needsInvoice', label: 'Nasce da una fattura', type: 'boolean' },
            { key: 'creditNote', label: 'Nota di credito', type: 'boolean' },
            { key: 'awaitingInvoice', label: 'Fattura da emettere', type: 'boolean' },
        ],
    },
    {
        key: 'stati-movimento',
        group: 'Tesoreria',
        name: 'Stati movimento',
        description: 'Quanto è certo un incasso o un pagamento. Solo «Pagato» è cassa reale; gli stati reali sono impegni certi, gli altri sono ipotesi, con la loro probabilità.',
        mode: 'direct',
        origin: 'Tesoreria (Tipologie)',
        codeAliases: ['COD'],
        columns: [
            { key: 'paid', label: 'Pagato', type: 'boolean' },
            { key: 'real', label: 'Reale', type: 'boolean' },
            { key: 'probability', label: 'Probabilità %', type: 'number' },
        ],
    },
    {
        key: 'conti',
        group: 'Tesoreria',
        name: 'Conti',
        description: 'Dove passano incassi e pagamenti: conti bancari (con estratto conto), carte appoggiate a un conto, cassa, e il conto virtuale delle compensazioni.',
        mode: 'direct',
        origin: 'Tesoreria (Banche)',
        codeAliases: ['COD'],
        columns: [
            {
                key: 'kind',
                label: 'Tipo',
                type: 'select',
                options: ['Banca', 'Carta', 'Cassa', 'Virtuale'],
                aliases: ['CC'],
            },
            { key: 'alias', label: 'Alias', type: 'text' },
            {
                key: 'backing',
                label: 'Conto di appoggio',
                type: 'text',
                lookup: { table: 'conti' },
            },
        ],
    },
    {
        key: 'pressione',
        group: 'Tesoreria',
        name: 'Pressione',
        description: 'Il sollecito che K-Rev subisce su un debito, da 1 (normalità) a 4 (azione legale): alza il fornitore nella lista dei pagamenti da fare.',
        mode: 'direct',
        origin: 'Tesoreria',
        columns: [],
    },
    {
        key: 'piano-riclassificato',
        group: 'Tesoreria',
        name: 'Piano riclassificato',
        description: 'Il conto economico gestionale: voci analitiche (ricavi, costi variabili, costi di struttura…) e voci aggregate con la loro formula (per esempio +RL-CVC). Ogni cliente e fornitore propone la sua voce ai documenti.',
        mode: 'direct',
        origin: 'Tesoreria (Riclassificato_Base)',
        codeAliases: ['Conto', 'RIC'],
        columns: [
            { key: 'sign', label: 'Segno', type: 'select', options: ['+', '-'] },
            { key: 'formula', label: 'Formula (aggregati)', type: 'text' },
        ],
    },
    {
        key: 'centri-costo',
        group: 'Tesoreria',
        name: 'Centri di costo',
        description: 'Aree a cui si attribuiscono costi e documenti: facoltativi.',
        mode: 'direct',
        origin: 'Tesoreria (INDICE)',
        columns: [],
    },
    {
        key: 'categorie-carte',
        group: 'Tesoreria',
        name: 'Categorie carte',
        description: 'Il tipo di negozio che la banca scrive su ogni spesa con carta (codice MCC: ristoranti, carburante, software…). Compagnie aeree, noleggi auto e alberghi hanno un codice ciascuno e qui stanno in tre gruppi (G300, G335, G350).',
        mode: 'direct',
        origin: 'Tesoreria (Codici_Carte_di_Credito), 10/10/2026',
        codeAliases: ['COD'],
        columns: [{ key: 'english', label: 'Descrizione inglese', type: 'text' }],
    },
    {
        key: 'codici-f24',
        group: 'Tesoreria',
        name: 'Codici F24',
        description: 'Cosa è ogni codice tributo o causale degli F24 di K-Rev e di che natura è. Un codice che manca compare in Tesoreria › F24 come «codice nuovo». Fonte: tabelle dei codici tributo dell’Agenzia delle Entrate.',
        mode: 'direct',
        origin: 'Tesoreria › F24, 02/11/2026',
        codeAliases: ['CODICE'],
        columns: [{ key: 'nature', label: 'Natura', type: 'text' }],
    },
    {
        key: 'motivi-sospensione',
        group: 'Tesoreria',
        name: 'Motivi di sospensione',
        description: 'Perché un movimento, una spesa con carta o una fattura resta in sospeso in Tesoreria › Da sistemare.',
        mode: 'direct',
        origin: 'Da sistemare, 30/10/2026',
        columns: [],
    },
    {
        key: 'parametri',
        group: 'Organizzazione',
        name: 'Parametri',
        description: 'I valori delle regole dell’organizzazione: soglia di approvazione dell’AD, termine di pagamento standard (il codice di Termini di pagamento), validità delle offerte in giorni, durata oltre la quale un contratto è complesso, in mesi, riferimento amministrativo proposto sulle offerte (l’e-mail di un utente).',
        mode: 'direct',
        origin: 'Allegato B §11, 03/10/2026',
        columns: [{ key: 'value', label: 'Valore', type: 'text' }],
    },
    {
        key: 'ruoli-aziendali',
        group: 'Organizzazione',
        name: 'Ruoli aziendali',
        description: 'L’organigramma di K-Rev: chi ricopre ogni ruolo e chi lo sostituisce. Più persone separate da «;», ognuna con (INT) o (EST). Sono ruoli dell’azienda, non i profili di accesso al programma.',
        mode: 'direct',
        origin: 'Allegato A, 03/10/2026',
        autoCode: 'RUO',
        writeRequires: 'organizzazione.roles',
        columns: [
            { key: 'parent', label: 'Riporta a', type: 'text', lookup: { table: 'ruoli-aziendali' } },
            { key: 'holders', label: 'Assegnato a', type: 'text' },
            { key: 'substitute', label: 'Sostituto', type: 'text' },
            { key: 'notes', label: 'Note', type: 'text' },
        ],
    },
    {
        key: 'fondi-pensione',
        group: 'Organizzazione',
        name: 'Fondi pensione',
        description: 'I fondi di previdenza complementare a cui i dipendenti destinano il TFR (ad esempio Amundi, Allianz).',
        mode: 'direct',
        origin: 'Persone, 07/10/2026',
        autoCode: 'FON',
        columns: [{ key: 'notes', label: 'Note', type: 'text' }],
    },
    {
        key: 'aree-progetti',
        group: 'Operations',
        name: 'Aree dei progetti',
        description: 'Le aree di K-Rev a cui appartengono i progetti: evoluzioni dell’app, marketing, sito… Chi vede e modifica i progetti di ogni area si sceglie in Operations › Progetti › Aree e accessi.',
        mode: 'direct',
        origin: 'Progetti, 26/10/2026',
        autoCode: 'ARE',
        columns: [{ key: 'notes', label: 'Note', type: 'text' }],
    },
    {
        key: 'soglie-indicatori',
        group: 'Operations',
        name: 'Soglie degli indicatori',
        description: 'Semafori della scheda «Indicatori» di commesse e progetti: VERDE fino al primo valore, GIALLO fino al secondo, rosso oltre. Per SPI e CPI al contrario: VERDE dal primo valore in su, GIALLO dal secondo.',
        mode: 'direct',
        origin: 'Indicatori, 27/10/2026',
        columns: [
            { key: 'green', label: 'Verde', type: 'number' },
            { key: 'yellow', label: 'Giallo', type: 'number' },
        ],
    },
];
/**
 * Tables no longer used, kept in the database until a later migration removes
 * them (migrations add first, remove later): out of the list and of the API.
 * «Referenti» gave way to the commerciale di riferimento, a user (05/10/2026);
 * «Piano riclassificato» to the plans of Controllo di gestione (08/10/2026).
 */
export const RETIRED_TABLES = ['referenti', 'piano-riclassificato'];
export function systemTable(key) {
    return SYSTEM_TABLES.find((table) => table.key === key);
}
/**
 * Prefix of the automatic codes of a table, or null where the code is written
 * by hand. A free table takes the first three letters of its name.
 */
export function autoCodePrefix(table) {
    if (table.kind === 'system')
        return systemTable(table.key)?.autoCode ?? null;
    const letters = table.name
        .normalize('NFD')
        .replace(/[^A-Za-z0-9]/g, '')
        .toUpperCase()
        .slice(0, 3);
    return letters.length > 0 ? letters : 'VOC';
}
/** The next automatic code after the ones there: COS-0001, COS-0002… */
export function nextAutoCode(prefix, codes) {
    const pattern = new RegExp(`^${prefix.replace(/[.-]/g, '\\$&')}-(\\d+)$`);
    let last = 0;
    for (const code of codes) {
        const match = pattern.exec(code);
        if (match)
            last = Math.max(last, Number(match[1]));
    }
    return `${prefix}-${String(last + 1).padStart(4, '0')}`;
}
export const VERSION_STATE_LABELS = {
    draft: 'Bozza',
    scheduled: 'Programmata',
    active: 'Attiva',
    archived: 'Archiviata',
};
export function versionState(version, today) {
    if (version.draft || !version.validFrom)
        return 'draft';
    if (version.validTo && version.validTo < today)
        return 'archived';
    if (version.validFrom > today)
        return 'scheduled';
    return 'active';
}
/** The version valid today, if any. */
export function activeVersion(versions, today) {
    return versions.find((version) => versionState(version, today) === 'active');
}
/**
 * What activating a draft from `validFrom` does: the version currently open
 * (no end date) ends the day before. Refused when a later version already
 * starts on or after that day.
 */
export function planActivation(versions, validFrom) {
    const published = versions.filter((version) => !version.draft && version.validFrom);
    const later = published.find((version) => version.validFrom >= validFrom);
    if (later) {
        return {
            error: `La versione ${later.number} vale già dal ${later.validFrom}: scegli una data successiva.`,
        };
    }
    const open = published.find((version) => version.validTo === null) ?? null;
    return { closes: open, closesOn: open ? addDays(validFrom, -1) : null };
}
/* --- Entries ----------------------------------------------------------------- */
/** Codes: upper-case letters, digits, dot, dash and underscore; stable once used. */
export const entryCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9._-]{0,29}$/, {
    message: 'Da 1 a 30 caratteri: lettere, cifre, punto, trattino',
});
/**
 * Values of the columns of an entry in canonical form: unknown keys dropped,
 * empty values null, each value checked against its column type.
 */
export function normaliseEntryValues(columns, values) {
    const result = {};
    const errors = {};
    for (const column of columns) {
        const raw = values[column.key];
        if (raw === undefined || raw === null || raw === '') {
            result[column.key] = column.type === 'boolean' ? false : column.type === 'tags' ? [] : null;
            continue;
        }
        switch (column.type) {
            case 'number':
            case 'money': {
                const number = parseDecimal(typeof raw === 'number' ? raw : String(raw));
                if (number === null)
                    errors[column.key] = 'Numero non valido';
                else
                    result[column.key] = column.type === 'money' ? Math.round(number * 100) / 100 : number;
                break;
            }
            case 'boolean':
                result[column.key] = raw === true || raw === 'true';
                break;
            case 'date': {
                const parsed = isoDateSchema.safeParse(raw);
                if (parsed.success)
                    result[column.key] = parsed.data;
                else
                    errors[column.key] = 'Data non valida';
                break;
            }
            case 'select':
                if (column.options && !column.options.includes(String(raw)))
                    errors[column.key] = 'Scelta non valida';
                else
                    result[column.key] = String(raw);
                break;
            case 'tags': {
                const list = Array.isArray(raw) ? raw.map(String) : [String(raw)];
                const invalid = list.filter((item) => column.options && !column.options.includes(item));
                if (invalid.length > 0)
                    errors[column.key] = `Scelta non valida: ${invalid.join(', ')}`;
                else
                    result[column.key] = column.options
                        ? column.options.filter((option) => list.includes(option))
                        : list;
                break;
            }
            default: {
                const text = String(raw).trim();
                if (text.length > 300)
                    errors[column.key] = 'Al massimo 300 caratteri';
                else
                    result[column.key] = text || null;
            }
        }
    }
    return { values: result, errors };
}
const entryFields = {
    description: requiredText(200),
    active: z.boolean().default(true),
    isDefault: z.boolean().default(false),
    hidden: z.boolean().default(false),
    values: z.record(z.string(), z.unknown()).default({}),
};
/** Body of `POST /api/tables/:key/entries`: added to the version being edited. */
/**
 * Body of `POST /api/tables/:key/entries`. The code may be left out in the
 * tables that give it by themselves (decision of 27/09/2026).
 */
export const createEntrySchema = z.object({
    code: z.preprocess((value) => (typeof value === 'string' && value.trim() === '' ? undefined : value), entryCodeSchema.optional()),
    ...entryFields,
});
const updateEntryFields = z
    .object({
    description: entryFields.description,
    active: z.boolean(),
    isDefault: z.boolean(),
    hidden: z.boolean(),
    values: z.record(z.string(), z.unknown()),
})
    .partial();
/** Body of `PATCH /api/tables/:key/versions/:number/entries/:code`: the code never changes. */
export const updateEntrySchema = updateEntryFields.refine((body) => Object.keys(body).length > 0, {
    message: 'Nessuna modifica',
});
/**
 * Body of `POST /api/tables/:key/versions/:number/entries/batch` (decision of
 * 01/10/2026): what was changed and added in the grid, saved all together or
 * not at all.
 */
export const batchEntriesSchema = z
    .object({
    updates: z
        .array(updateEntryFields.extend({ code: entryCodeSchema }))
        .max(1000)
        .default([]),
    creates: z.array(createEntrySchema).max(1000).default([]),
})
    .refine((body) => body.updates.length + body.creates.length > 0, {
    message: 'Nessuna modifica',
});
/**
 * Where an error of a batch belongs, as the key of `fieldErrors`: the code of
 * a voice changed, `nuova-<n>` for the n-th voice added, then the field
 * («COS-0003.values.cost», «nuova-0.description»).
 */
export function batchErrorKey(ref, field) {
    return `${typeof ref === 'number' ? `nuova-${ref}` : ref}.${field}`;
}
/** Body of `POST /api/tables`: a free table. */
export const createTableSchema = z.object({
    name: requiredText(100),
    description: optionalText(300),
    mode: z.enum(['direct', 'versioned']),
    isClassification: z.boolean().default(false),
    columns: z
        .array(z.object({
        label: requiredText(60),
        type: z.enum(COLUMN_TYPES),
        options: z.array(requiredText(60)).max(50).optional(),
    }))
        .max(12, { message: 'Al massimo 12 colonne' }),
});
/** Body of `POST /api/tables/:key/versions/:number/entries/:code/move`. */
export const moveEntrySchema = z.object({ direction: z.enum(['up', 'down']) });
export const newVersionSchema = z.object({ from: z.number().int().positive() });
export const activateVersionSchema = z.object({ validFrom: isoDateSchema });
//# sourceMappingURL=reference-tables.js.map